const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const docketPageHtml = (booking, index, total, helpers) => {
  const {
    bookingSurname,
    vehicleLabel,
    productTypeCode,
    terminalNumber,
    formatHeroDate,
    formatTime,
    formatTapDate,
    formatIsoDate,
    qrImageUrl,
  } = helpers;

  const code = productTypeCode(booking.productType);
  const typeLabel = code === "SELF" ? "SP" : code;
  const surname = bookingSurname(booking.name);
  const vehicle = vehicleLabel(booking);
  const returnTime = formatTime(booking.returnDateTime);
  const returnHero = formatHeroDate(booking.returnDateTime);
  const returnIso = formatIsoDate(booking.returnDateTime);
  const returnDate = formatTapDate(booking.returnDateTime);
  const arrivalStamp = `${formatTapDate(booking.arrivalDateTime)} ${formatTime(
    booking.arrivalDateTime
  )}`;
  const returnTerminal = terminalNumber(booking.returnTerminal);
  const qr = qrImageUrl(booking.referenceNumber || booking._id);
  const printedAt = new Date().toLocaleString("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

  return `
    <section class="page">
      <div class="meta-top">
        <span>${escapeHtml(printedAt)}</span>
        <span>Print</span>
        <span></span>
      </div>
      <div class="grid">
        <div class="col left">
          <img class="qr qr-sm" src="${qr}" alt="QR" />
          <div class="stack">
            <div class="name">${escapeHtml(surname)}</div>
            <div class="ref">${escapeHtml(booking.referenceNumber || "—")}</div>
            <div>${escapeHtml(booking.mobile || "—")}</div>
          </div>
          <div class="stack tight">
            <div class="reg">${escapeHtml(booking.reg || "TBC")}</div>
            <div>${escapeHtml(vehicle)}</div>
          </div>
          <div class="stack tight">
            <div>${escapeHtml(arrivalStamp)}</div>
            <div>${escapeHtml(returnDate)}</div>
            <div>${escapeHtml(booking.rtnFlight || booking.outFlight || "TBC")}</div>
            <div>${escapeHtml(booking.colour || "—")}</div>
          </div>
          <div class="mini-col">
            <div>${escapeHtml(returnTerminal)}</div>
            <div>${escapeHtml(returnTime)}</div>
            <div>${escapeHtml(returnTerminal)}</div>
          </div>
        </div>
        <div class="col center">
          <img class="qr qr-lg" src="${qr}" alt="QR" />
          <div class="hero-date">${escapeHtml(returnHero)}</div>
          <div class="hero-time">${escapeHtml(returnTime)}</div>
          <div class="hero-term">${escapeHtml(returnTerminal)}</div>
          <div class="hero-type">${escapeHtml(typeLabel)}</div>
        </div>
        <div class="col right">
          <div class="reg">${escapeHtml(booking.reg || "TBC")}</div>
          <div class="stack tight right-stack">
            <div>${escapeHtml(vehicle)}</div>
            <div>${escapeHtml(booking.colour || "—")}</div>
            <div>${escapeHtml(returnTime)}</div>
            <div>${escapeHtml(returnIso)}</div>
            <div>${escapeHtml(returnTerminal)}</div>
            <div>${escapeHtml(booking.rtnFlight || booking.outFlight || "TBC")}</div>
            <div>${escapeHtml(typeLabel)}</div>
            <div>${escapeHtml(booking.referenceNumber || "—")}</div>
          </div>
        </div>
      </div>
      <div class="bottom">
        <span>${escapeHtml(booking.referenceNumber || "—")}</span>
        <span>${escapeHtml(booking.reg || "TBC")}</span>
        <span>${escapeHtml(vehicle)}</span>
        <span>${escapeHtml(booking.colour || "—")}</span>
      </div>
      <div class="footer">
        <span>TAP departure docket</span>
        <span>${index + 1}/${total}</span>
      </div>
    </section>
  `;
};

export const buildDocketsHtml = (bookings, dateKey, helpers) => {
  const pages = bookings
    .map((booking, index) => docketPageHtml(booking, index, bookings.length, helpers))
    .join("");

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>TAP-dockets-${escapeHtml(dateKey)}</title>
  <style>
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      background: #fff;
      color: #111;
      font-family: Arial, Helvetica, sans-serif;
    }
    .page {
      width: 210mm;
      min-height: 297mm;
      padding: 12mm 14mm 16mm;
      page-break-after: always;
      position: relative;
    }
    .page:last-child { page-break-after: auto; }
    .meta-top {
      display: flex;
      justify-content: space-between;
      font-size: 11px;
      color: #444;
      margin-bottom: 8mm;
    }
    .grid {
      display: grid;
      grid-template-columns: 28% 44% 28%;
      gap: 8mm;
      min-height: 220mm;
    }
    .col { display: flex; flex-direction: column; }
    .center { align-items: center; text-align: center; }
    .right { align-items: flex-end; text-align: right; }
    .qr { display: block; }
    .qr-sm { width: 28mm; height: 28mm; }
    .qr-lg { width: 52mm; height: 52mm; margin-bottom: 8mm; }
    .stack { margin: 8mm 0; line-height: 1.35; }
    .stack.tight { margin: 6mm 0; }
    .name { font-size: 22px; font-weight: 700; }
    .ref { font-size: 16px; font-weight: 700; }
    .reg { font-size: 20px; font-weight: 800; letter-spacing: 0.4px; }
    .mini-col { margin-top: auto; font-size: 16px; font-weight: 700; line-height: 1.5; }
    .hero-date { font-size: 54px; font-weight: 800; line-height: 1; margin-top: 6mm; }
    .hero-time { font-size: 48px; font-weight: 800; line-height: 1.1; margin: 3mm 0 8mm; }
    .hero-term { font-size: 110px; font-weight: 800; line-height: 0.9; }
    .hero-type { font-size: 36px; font-weight: 800; letter-spacing: 2px; }
    .right-stack { font-size: 16px; font-weight: 600; }
    .bottom {
      display: flex;
      justify-content: space-between;
      gap: 8px;
      margin-top: 8mm;
      font-size: 14px;
      font-weight: 700;
    }
    .footer {
      position: absolute;
      left: 14mm;
      right: 14mm;
      bottom: 8mm;
      display: flex;
      justify-content: space-between;
      font-size: 11px;
      color: #555;
    }
    @media print {
      .page { margin: 0; }
    }
    @page { size: A4 portrait; margin: 0; }
  </style>
</head>
<body>
  ${pages}
</body>
</html>`;
};

const waitForDocumentImages = (doc) =>
  Promise.all(
    Array.from(doc.images || []).map((img) =>
      img.complete
        ? Promise.resolve()
        : new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
          })
    )
  );

export const printDepartureDockets = async (bookings, dateKey, helpers) => {
  const html = buildDocketsHtml(bookings, dateKey, helpers);
  const existing = document.getElementById("tap-dockets-print-frame");
  if (existing) existing.remove();

  const iframe = document.createElement("iframe");
  iframe.id = "tap-dockets-print-frame";
  iframe.title = "TAP dockets";
  iframe.setAttribute(
    "style",
    "position:fixed;top:0;left:-10000px;width:210mm;height:297mm;border:0;background:#fff;"
  );
  document.body.appendChild(iframe);

  const frameWindow = iframe.contentWindow;
  const frameDoc = iframe.contentDocument || frameWindow.document;
  frameDoc.open();
  frameDoc.write(html);
  frameDoc.close();

  await waitForDocumentImages(frameDoc);
  await new Promise((resolve) => setTimeout(resolve, 250));
  frameWindow.focus();
  frameWindow.print();

  const cleanup = () => {
    setTimeout(() => iframe.remove(), 1500);
  };
  if (frameWindow.addEventListener) {
    frameWindow.addEventListener("afterprint", cleanup);
  }
  setTimeout(cleanup, 60000);
};
